import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, ArrowLeft, Mail, Phone, FileText, Download, Share2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const { invoiceId } = useParams();
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  
  // Verify payment and trigger status update + emails when page loads
  useEffect(() => {
    if (invoiceId) {
      verifyPayment();
    } else {
      setVerifying(false);
    }
  }, [invoiceId]);
  
  const verifyPayment = async () => {
    setVerifying(true);
    setVerificationError(null);
    
    try {
      const response = await fetch(`${API}/api/pay/${invoiceId}/verify-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();
      
      if (data.status === "paid" || data.paid === true) {
        setPaymentVerified(true);
      } else {
        // Payment might still be processing, retry after delay
        setTimeout(() => {
          retryVerification();
        }, 2000);
      }
    } catch (error) {
      setVerificationError("Could not verify payment status");
      // Still show success page, payment likely went through
      setPaymentVerified(true);
    } finally {
      setVerifying(false);
    }
  };
  
  const retryVerification = async () => {
    try {
      const response = await fetch(`${API}/api/pay/${invoiceId}/verify-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();
      if (data.status === "paid" || data.paid === true) {
        setPaymentVerified(true);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };
  
  const handleDownloadReceipt = async () => {
    if (!invoiceId) {
      toast.error("Invoice ID not found");
      return;
    }
    
    if (!paymentVerified) {
      toast.error("Please wait for payment verification to complete");
      return;
    }
    
    setDownloading(true);
    try {
      const response = await fetch(`${API}/api/pay/${invoiceId}/receipt`);
      
      if (!response.ok) {
        // For error responses, try to get the error message
        let errorMessage = "Failed to download receipt";
        try {
          // Clone the response to safely read it
          const clonedResponse = response.clone();
          const errorData = await clonedResponse.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Receipt downloaded successfully");
    } catch (error) {
      toast.error(error.message || "Failed to download receipt");
    } finally {
      setDownloading(false);
    }
  };
  
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareText = `Payment receipt for Invoice #${invoiceId} from RNB Consulting Group`;
    
    // Check if Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Payment Receipt - RNB Consulting Group",
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          // User cancelled, that's okay
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };
  
  const copyToClipboard = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };
  
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-lg" data-testid="payment-success-card">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" data-testid="success-icon" />
          </div>
          
          <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2" data-testid="success-title">
            Payment Successful!
          </h1>
          
          <p className="text-rnb-gray mb-6" data-testid="success-message">
            Thank you for your payment. A confirmation email has been sent to your registered email address.
          </p>
          
          {invoiceId && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 font-medium">
                Invoice #{invoiceId} has been paid
              </p>
              {verifying && (
                <p className="text-xs text-green-600 mt-2 flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Verifying payment...
                </p>
              )}
            </div>
          )}
          
          {/* Receipt Actions */}
          {invoiceId && (
            <div className="flex gap-3 mb-6">
              <Button 
                onClick={handleDownloadReceipt}
                disabled={downloading || verifying}
                className="flex-1 bg-rnb-teal hover:bg-rnb-teal/90"
                data-testid="download-receipt-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Receipt
                  </>
                )}
              </Button>
              <Button 
                onClick={handleShare}
                variant="outline"
                className="px-4"
                data-testid="share-receipt-btn"
              >
                {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
              </Button>
            </div>
          )}

          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-rnb-gray">
              If you have any questions about your payment or invoice, please don&apos;t hesitate to contact us.
            </p>
          </div>

          <div className="space-y-3 mb-8">
            <a 
              href="mailto:info@rnbconsultinggroup.com" 
              className="flex items-center justify-center gap-2 text-rnb-blue hover:text-rnb-navy transition-colors"
              data-testid="contact-email"
            >
              <Mail className="w-4 h-4" />
              info@rnbconsultinggroup.com
            </a>
            <a 
              href="tel:888-250-4605" 
              className="flex items-center justify-center gap-2 text-rnb-blue hover:text-rnb-navy transition-colors"
              data-testid="contact-phone"
            >
              <Phone className="w-4 h-4" />
              (888) 250-4605
            </a>
          </div>

          <div className="space-y-3">
            <Link to="/portal/invoices">
              <Button className="w-full btn-hover-lift" variant="default" data-testid="view-invoices-btn">
                <FileText className="mr-2 h-4 w-4" />
                View Invoice History
              </Button>
            </Link>
            
            <Link to="/">
              <Button className="w-full" variant="outline" data-testid="return-home-btn">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Homepage
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
